/*******************************************************
* Date Utilities Library
* 
* A date utility library providing timezone-safe date formatting functions
* specifically for MySQL Date column interactions.
* 
* Key Features:
* - Timezone-safe date-to-string conversion for API requests
* - UTC database timestamp conversion for local display
* - Prevents UTC conversion issues with MySQL Date columns
* - Dual usage patterns: functional and prototype extension
* 
* Primary Operations:
* - toLocalDateString(): Converts Date to YYYY-MM-DD without timezone conversion
* - convertTimestampValue(): Converts timestamps between UTC and browser-local time
* 
* Usage:
* ```typescript
* import { toLocalDateString } from './dateUtilsLib';
* import './dateUtilsLib'; // For prototype extension
* 
* // Method 1: Functional usage
* const value = toLocalDateString(selectedWeekEnd);
* 
* // Method 2: Prototype usage (after import)
* const value = selectedWeekEnd.toLocalDateString();
* ```
********************************************************/

//########## IMPORTS ##########
//#region IMPORTS
import dayjs, { type Dayjs } from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekday from 'dayjs/plugin/weekday';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en';
//#endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
declare global {
    interface Date {
        /**
         * Converts Date to YYYY-MM-DD string format without timezone conversion.
         * Specifically designed for MySQL Date column comparisons.
         * @returns Date string in YYYY-MM-DD format
         */
        toLocalDateString(): string;
    }
}

export type TimestampDirection = 'fromUTC' | 'toUTC';

interface TimestampConversionOptions {
    preserveInvalid?: boolean;
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
const SUNDAY_FIRST_CALENDAR_LOCALE = 'en-sunday-first';

// Configure dayjs plugins - centralized configuration for entire application
dayjs.extend(weekOfYear);
dayjs.extend(weekday);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

// Set locale to English (can be changed as needed)
dayjs.locale('en');

// Keep visual date-picker calendars Sunday-first without changing the active
// job's locale, which drives business-week calculations through TS_WeekStart.
dayjs.locale({
    ...dayjs.Ls.en,
    name: SUNDAY_FIRST_CALENDAR_LOCALE,
    weekStart: 0,
}, undefined, true);
//#endregion


//########## FUNCTIONS ##########
//#region FUNCTIONS
/**
 * Converts a Date object to YYYY-MM-DD string format without timezone conversion.
 * 
 * This function is specifically designed for MySQL Date column comparisons where
 * timezone conversion would cause incorrect date matching. Unlike toISOString(),
 * this function preserves the local date components exactly as they appear to the user.
 * 
 * @param date - The Date object to format
 * @returns Date string in YYYY-MM-DD format
 * 
 * @example
 * ```typescript
 * const date = new Date('2025-09-27T23:59:59-05:00'); // Sep 27, 2025 in CDT
 * const result = toLocalDateString(date); // Returns "2025-09-27"
 * 
 * // Compare with problematic toISOString():
 * const problematic = date.toISOString().split('T')[0]; // Returns "2025-09-28" (wrong!)
 * ```
 */
function toLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Converts a timestamp between UTC and browser-local SQL timestamp formats.
 * MySQL timestamp strings do not include a zone, so UTC values are parsed explicitly.
 */
function convertTimestampValue(
    value: unknown,
    direction: TimestampDirection,
    { preserveInvalid = false }: TimestampConversionOptions = {},
): unknown {
    if (value == null) return value;

    const isString = typeof value === 'string';
    const isDate = value instanceof Date;
    if (!isString && !isDate) return value;

    const parsed = direction === 'fromUTC'
        ? dayjs.utc(value)
        : dayjs(value);

    if (preserveInvalid && !parsed.isValid()) return value;

    return (direction === 'fromUTC' ? parsed.local() : parsed.utc())
        .format('YYYY-MM-DD HH:mm:ss');
}

// Extend Date prototype to support method chaining
Date.prototype.toLocalDateString = function (): string {
    return toLocalDateString(this);
};

/**
 * Returns the logical "shift date" as a Dayjs anchored to start-of-day.
 *
 * A shift that crosses midnight still belongs to the calendar date it *started*
 * on, until `TS_ShiftEndHrsAfterMidnight` hours past midnight have elapsed.
 * For example, with hrsAfterMidnight = 6, anything before 6 AM rolls back to
 * yesterday's shift.
 *
 * Mirrors the SQL pattern used elsewhere:
 *   DATE(NOW() - INTERVAL <TS_ShiftEndHrsAfterMidnight> HOUR)
 *
 * @param hrsAfterMidnight - Value of the TS_ShiftEndHrsAfterMidnight setting
 * @param now              - Override for "current time" (testing); defaults to dayjs()
 * @returns Dayjs anchored to start-of-day for the logical shift date
 */
function shiftNow(hrsAfterMidnight: number, now: Dayjs = dayjs()): Dayjs {
    return now.subtract(hrsAfterMidnight || 0, 'hour').startOf('day');
}

//#endregion

//########## EXPORTS ##########
//#region EXPORTS
export {
    SUNDAY_FIRST_CALENDAR_LOCALE,
    toLocalDateString,
    convertTimestampValue,
    shiftNow,
    dayjs as default
};

// Also export dayjs as named export for convenience
export { dayjs };
//#endregion
